/**
 * Declarative config of `extranet.pendingPiecesBanner` (Accueil « pièces en
 * attente » banner, fiche 22080). Every text lives here (never inline JSX):
 *
 * - `title` — the headline, a template whose `{count}` placeholder is
 *   replaced by the bold « {n} pièce(s) » fragment; it is the component title
 *   (a `setTitle` rule rewrites it — keep `{count}` to show the number);
 * - `items` — the secondary line, stable key `description` (rename = new
 *   text, hide);
 * - `actions` — the « Voir la liste » link, stable action key `viewList`
 *   (`setActionLabel` / `hideAction`).
 */

import type { InfoItemDescriptor } from '@components/shared/configurable';

/** Registered id of the banner. */
export const PENDING_PIECES_BANNER_ID = 'extranet.pendingPiecesBanner';

/** Placeholder of the count fragment in the title template. */
export const COUNT_PLACEHOLDER = '{count}';

/** Base headline (component title). */
export const PENDING_PIECES_BANNER_TITLE = `Vous avez ${COUNT_PLACEHOLDER} en attente de dépôt`;

/** Noun of the bold count fragment (« 1 pièce », « 5 pièces »). */
export const PENDING_PIECES_UNIT = { one: 'pièce', other: 'pièces' } as const;

/** Stable key of a banner text item (rule contract). */
export type PendingPiecesBannerItemKey = 'description';

/** One text line of the banner (declarative config). */
export interface PendingPiecesBannerItem extends InfoItemDescriptor {
  key: PendingPiecesBannerItemKey;
  /** The text itself (overridable with a rename rule). */
  label: string;
  order: number;
}

/** The banner's text lines below the headline. */
export const PENDING_PIECES_BANNER_ITEMS: readonly PendingPiecesBannerItem[] = [
  {
    key: 'description',
    label: 'Des documents sont attendus pour finaliser le traitement de vos dossiers.',
    order: 0,
  },
];

/** Stable key of the banner's link (rule contract of the `actions` bucket). */
export const VIEW_LIST_ACTION_KEY = 'viewList';

/** The banner's link. */
export const PENDING_PIECES_BANNER_ACTION = {
  key: VIEW_LIST_ACTION_KEY,
  label: 'Voir la liste',
  /** Sub-path under `/extranet/:tenantSlug`. */
  to: 'pieces-attendues',
} as const;

/** « {n} pièce(s) » — the bold count fragment of the headline. */
export function pendingPiecesCountText(count: number): string {
  return `${String(count)} ${count === 1 ? PENDING_PIECES_UNIT.one : PENDING_PIECES_UNIT.other}`;
}
